import fetcher, {
	FleetFile,
	FleetGetListParams,
	FleetPaginated,
	FleetQueryParams,
} from '../fetcher';
import { MasterData } from './master-data';
import { ShareholderSlikInformation, SlikRobotikDetailArray } from './prospect';
import { SurveyInformation } from './survey';
import {
	CustomerBusinessDocument,
	CustomerLegalDocument,
	CustomerManagement,
	CustomerReferralDocument,
	CustomerShareholder,
	FileData,
	FilePayload,
	MainInformation,
	ProspectEmploymentContract,
} from './types';

export type NAPList = {
	no: number;
	prospectId: string;
	customer: MasterData;
	pipelineId: number;
	customerGroup: MasterData;
	plafonType: MasterData;
	orderSource: MasterData;
	status: null;
	dateOfInput: string;
	prospectStep: string;
	reviewStatus: string;
	napStatus: string;
	pipelineStatus: string;
};

export const getNAPList = (body: FleetGetListParams) => {
	return fetcher<FleetPaginated<NAPList>>({ api: '/naps/get-list', body });
};

export type NAPState = {
	action: 'ADD' | 'UPDATE' | 'VIEW' | 'DISABLED';
	status: 'NOT_SUBMITTED' | 'SUBMITTED' | 'VERIFIED';
};

export type NAPDetail = {
	plafonType: 'JOIN_FINANCING' | 'NON_JOIN_FINANCING';
	backgroundInformation: NAPState;
	companyAndGroupInformation: NAPState;
	financialAnalysis: NAPState;
	internalCompliance: NAPState;
	recommendation: NAPState;
	riskAndMitigation: NAPState;
	specialConditionSubmission: NAPState;
	wholesale: NAPState;
	summaryConsent: NAPState;
	eddPepAnalysis: NAPState;
	napId: number;
	napNo: string;
	napInformation: {
		approvalHistories: null;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerReferralDocument[];
		surveyInformation: SurveyInformation;
		mainInformation: MainInformation;
		pipelineInformation: {
			pipelineId: number;
			pipelineNo: string;
			pipelineStatus: string;
			ultimateOwner: string;
		};
	};
	approvalDetail: {
		actionOptions: MasterData[];
		componentOptions: MasterData[];
		hasApprovalPermission: boolean;
		napApprovalHistory: {
			date: string;
			id: number;
			name: string;
			notes: string;
			process: string;
			status: string;
		}[];
	};
};

export const getNAPDetail = (prospectId: string) => {
	return fetcher<NAPDetail>({ api: '/naps/get-detail', body: { prospectId } });
};

export type NAPWholesaleDetail = {
	id: number;
	napId: number;
	status: string;
	subject: MasterData;
	subjectOptions: MasterData[];
	pipelineSummaryDate: string;
	applicantName: string;
	debtorType: MasterData;
	debtorTypeOptions: MasterData[];
	debtorFappDate: string;
	establishmentYear: string;
	industrySector: string;
	businessGroupName: string;
	bmriReferralProviderName: string;
	consentApprovalNumber: string;
	consentApprovalDate: string;
	restruGpDueDate: string;
	restruGpCovidStatus: MasterData;
	restruGpCovidStatusOptions: MasterData[];
	restruGpStatus: MasterData;
	restruGpStatusOptions: MasterData[];
	referralFormDate: string;
	contracts: ProspectEmploymentContract[];
	operationalYear: string;
	bmriReferralInformation: string;
	authorityLevel: MasterData;
	authorityLevelOptions: MasterData[];
	regulationReference: string;
	regulationReferenceOptions: MasterData[];
	proposers: NAPUser[];
	recommendingCommittees: NAPUser[];
	plafonTypeOptions: MasterData[];
	totalFacilityRequest: {
		debtorPlafons: {
			applicationLimit: number;
			existingLimit: number;
			totalLimit: number;
			expiredDate: string;
			plafonType: MasterData;
		}[];
		debtorTotal: {
			applicationLimit: number;
			existingLimit: number;
			totalLimit: number;
		};
		businessGroupTotal: {
			applicationLimit: number;
			existingLimit: number;
			totalLimit: number;
		};
		jfTotalExposure: {
			applicationLimit: number;
			existingLimit: number;
			totalLimit: number;
		};
		njfTotalExposure: {
			applicationLimit: number;
			existingLimit: number;
			totalLimit: number;
		};
		grandTotal: {
			applicationLimit: number;
			existingLimit: number;
			totalLimit: number;
		};
		businessGroups: {
			name: string;
			expiredDate: string;
			existingLimit: number;
			applicationLimit: number;
			totalLimit: number;
			plafonType: MasterData;
			customerId: number;
			isDefault: boolean;
		}[];
	};
	agingDate: string;
	notes: string;
	verificationNotes: string;
	wholesaleId?: number;
};

export const getNAPWholesaleDetail = (prospectId: string) => {
	return fetcher<NAPWholesaleDetail>({
		api: '/naps/get-wholesale-detail',
		body: { prospectId },
	});
};

export type NAPWholesalePayload = {
	napId: number;
	wholesaleId: number;
	prospectId: string;
	debtorFappDate: string;
	referralFormDate: string;
	bmriReferralInformation: string;
	authorityLevel: string;
	regulationReference: string;
	additionalProposers: { id: number }[];
	additionalRecommendingCommittees: { id: number }[];
	totalFacilityRequest: {
		debtorPlafons?: {
			isDefault: boolean;
			existingLimit: number;
			applicationLimit: number;
			totalLimit: number;
			plafonType: number;
			expiredDate: string;
		}[];
		debtor: {
			initialAmount: number;
			proposedAmount: number;
			finalAmount: number;
		};
		businessGroups: {
			isDefault: boolean;
			existingLimit: number;
			applicationLimit: number;
			totalLimit: number;
			customerId?: number;
			name: string;
			plafonType: number;
			expiredDate: string;
		}[];
	};
	agingDate: string;
	notes: string;
	contracts?: ProspectEmploymentContract[];
	restruGpCovidStatus?: number;
	restruGpDueDate?: string;
};

export const NAPSubmitWholesale = (body: NAPWholesalePayload) => {
	return fetcher({ api: '/naps/frm/submit-wholesale', body });
};

export type NAPWholesaleJFPayload = {
	napId: number;
	wholesaleId: number;
	prospectId: string;
	establishmentYear: string;
	operationalYear: string;
	consentApprovalNumber: string;
	consentApprovalDate: string;
	restruGpStatus: number;
	restruGpCovidStatus: number;
	restruGpDueDate: string;
	authorityLevel: string;
	regulationReference: string;
	additionalProposers: { id: number }[];
	additionalRecommendingCommittees: { id: number }[];
	totalFacilityRequest: {
		debtorPlafons?: {
			existingLimit: number;
			applicationLimit: number;
			totalLimit: number;
			plafonType: number;
			expiredDate: string;
		}[];
		businessGroups: {
			existingLimit: number;
			applicationLimit: number;
			totalLimit: number;
			customerId?: number;
			name: string;
			plafonType: number;
			expiredDate: string;
		}[];
	};
	notes: string;
	verificationNotes?: string;
	agingDate: string;
	contracts: {
		id: number;
		prospectId: string;
		bouwheerId: string;
		jobTitle: string;
		spkNumber: string;
		jobLocation: string;
		termOfPayment: string;
		contractDocument: FilePayload;
		contractDetail: string;
		contractValue: number;
		startDate: string;
		endDate: string;
		tenor?: string;
		paymentMethod?: number;
	}[];
};

export const NAPSubmitWholesaleJF = (body: NAPWholesaleJFPayload) => {
	return fetcher({ api: '/naps/frm/jf/submit-wholesale', body });
};

export const NAPVerifyWholesale = (
	body: NAPWholesalePayload & { verificationNotes: string },
) => {
	return fetcher({ api: '/naps/cca/verify-wholesale', body });
};

export const NAPVerifyWholesaleJF = (
	body: NAPWholesaleJFPayload & { verificationNotes: string },
) => {
	return fetcher({ api: '/naps/cca/jf/verify-wholesale', body });
};

export type NAPBackgroundInfo = {
	id: number;
	napId: number;
	prospectId: string;
	financingAndUnitRequirements: {
		id: number;
		numberOfUnits: number;
		price: number;
		unitBrandDetails: string;
		projectLocation: string;
	}[];
	finalApproverOptions: MasterData[];
	specialConditionSubmission: {
		id: number;
		subject: string;
		provisions: string;
		approvalDate: string;
		finalApprover: { id: number; value: string }[];
	}[];
	specialConditionSubmissions: MasterData[];
	specialConditionStatusOptions: MasterData[];
	specialConditionSubjectOptions: MasterData[];
	notes: string;
	verificationNotes: string;
	status: string;
};

export const getNAPBackgroundInfo = (prospectId: string) => {
	return fetcher<NAPBackgroundInfo>({
		api: '/naps/get-background-information',
		body: { prospectId },
	});
};

export type NAPBackgroundInfoPayload = {
	id?: number;
	napId: number;
	prospectId: string;
	financingAndUnitRequirements: {
		numberOfUnits: number;
		price: number;
		unitBrandDetails: string;
		projectLocation: string;
	}[];
	specialConditionSubmission: {
		id?: string;
		subjectId: number;
		provisions: string;
		proposal: string;
		specialCondition: string;
		specialConditionStatusId: number;
		finalApprover: number[];
		evidenceFile: FilePayload;
	}[];
	notes: string;
};

export const submitNAPBackgroundInfo = (body: NAPBackgroundInfoPayload) => {
	return fetcher({ api: '/naps/frm/submit-background-information', body });
};

export const verifyNAPBackgroundInfo = (
	body: NAPBackgroundInfoPayload & { verificationNotes?: string },
) => {
	return fetcher({ api: '/naps/cca/verify-background-information', body });
};

export type NAPCompanyInformation = {
	id: number;
	napId: number;
	prospectId: string;
	relationshipYear: string;
	financedSectorBusinessStart: string;
	numberOfEmployees: number;
	bmpp: MasterData;
	bmppOptions: MasterData[];
	businessEntity: MasterData;
	ultimateBeneficialOwner: string;
	exposureDocument: FileData;
	companyTreeDocument: FileData;
	companyBackground: string;
	companyEstablishmentDate: string;
	yearlyBusinessDevelopment: string;
	companyManagements: CustomerManagement[];
	companyShareholders: CustomerShareholder[];
	companyGroups: {
		isDefault: boolean;
		companyGroupGeneralInformation: {
			id: number;
			entityName: string;
			industrySector: MasterData;
			relationshipYear: string;
			keyPerson: string;
		};
		managementStructures: {
			id: number;
			name: string;
			positionId: MasterData;
			shareholderId: number;
			isShareholder: boolean;
			shareholding: number;
			shareAmount: number;
			shareValue: number;
		}[];
		shareholderStructures: {
			id: number;
			name: string;
			positionId: MasterData;
			managementId: number;
			isManagement: boolean;
			shareholding: number;
			shareAmount: number;
			shareValue: number;
		}[];
	}[];
	notes: string;
	verificationNotes: string;
	latestManagementDeedDocument: FileData;
	latestManagementDeedNumber: string;
	latestManagementDeedYear: string;
	latestShareholderDeedDocument: FileData;
	latestShareholderDeedNumber: string;
	latestShareholderDeedYear: string;
	action: string;
	status: string;
	debtorName: string;
	address: string;
	rt: string;
	rw: string;
	postal: {
		postalId: number;
		postalCode: string;
		locationName: string;
		districtName: string;
		cityName: string;
		provinceName: string;
	};
	industrySector: string;
	keyPerson: string;
	position: MasterData;
};

export const getNAPCompanyInformation = (prospectId: string) => {
	return fetcher<NAPCompanyInformation>({
		api: '/naps/get-company-information-detail',
		body: { prospectId },
	});
};

export type NAPCompanyGroupPayload = {
	generalInformation: {
		entityName: string;
		businessField: string;
		relationshipYear: string;
		keyPerson: string;
		latestManagementShareholderDeedNumber: string;
		latestManagementShareholderDeedYear: string;
	};
	managementStructure: CustomerManagement<'payload'>[];
	shareholderStructure: CustomerShareholder<'payload'>[];
};

export type NAPCompanyInformationPayload = {
	id?: number;
	napId: number;
	prospectId: string;
	relationshipYear: string;
	financedSectorBusinessStart: string;
	numberOfEmployees: number;
	bmppId: number;
	ultimateBeneficialOwner: string;
	exposureDocument: FilePayload;
	companyTreeDocument: FilePayload;
	companyBackground: string;
	yearlyBusinessDevelopment: string;
	companyManagements: CustomerManagement<'payload'>[];
	companyShareholders: CustomerShareholder<'payload'>[];
	companyGroups: {
		generalInformation: {
			id: number;
			entityName: string;
			industrySectorId: number;
			relationshipYear: string;
			keyPerson: string;
		};
		managementStructure: {
			id: number;
			name: string;
			position: number;
			isShareholder: boolean;
			shareholding: number;
			shareAmount: number;
			shareValue: number;
		}[];
		shareholderStructure: {
			id: number;
			name: string;
			position: number;
			shareholding: number;
			shareAmount: number;
			shareValue: number;
		}[];
	}[];
	notes: string;
	latestShareholderDeedNumber: string;
	latestShareholderDeedYear: string;
	latestShareholderDeedDocument: FilePayload;
	latestManagementDeedNumber: string;
	latestManagementDeedYear: string;
	latestManagementDeedDocument: FilePayload;
};

export const submitNAPCompanyInformation = (
	body: NAPCompanyInformationPayload,
) => {
	return fetcher({ api: '/naps/frm/submit-company-information', body });
};

export const verifyNAPCompanyInformation = (
	body: NAPCompanyInformationPayload & { verificationNotes: string },
) => {
	return fetcher({ api: '/naps/cca/verify-company-information', body });
};

export type NAPInternalComplianceDetail = {
	internalComplianceId: number;
	id: number;
	napId: number;
	notes: string;
	verificationNotes: string;
	portofolioGuideline: string;
	additionalPortofolioGuideline: string;
	customerRating: MasterData;
	customerRatingOptions: MasterData[];
	customerRatingEvidenceDocument: FileData;
	customerRatingInformation: string;
	bouwheerRating: MasterData;
	bouwheerRatingOptions: MasterData[];
	bouwheerRatingEvidenceDocument: FileData;
	bouwheerRatingInformation: string;
	eddHighRiskAndPepAnalysis: string;
	cpaPatDocument: FileData;
	cpaPatInformation: string;
	isEddHighRiskAndPepAnalysisMandatory: true;
	slikResult: SlikRobotikDetailArray;
	manualSlikResult: {
		managementSlikInformation: ShareholderSlikInformation[];
		shareholderSlikInformation: ShareholderSlikInformation[];
		slikCheck: {
			slikFormFile: FileData;
			applicationLetterFile: FileData;
			slikCheckingStatus?: MasterData;
			slikCheckingDate?: string;
		};
	};
};

export const getNAPInternalComplianceDetail = (prospectId: string) => {
	return fetcher<NAPInternalComplianceDetail>({
		api: '/naps/get-internal-compliance',
		body: { prospectId },
	});
};

export type NAPInternalCompliancePayload = {
	internalComplianceId: number;
	napId: number;
	notes: string;
	verificationNotes: string;
	additionalPortofolioGuideline: string;
	customerRatingId: number;
	customerRatingEvidenceDocumentId: FilePayload;
	customerratingInformation: string;
	bouwheerRatingId: number;
	bouwheerRatingEvidenceDocumentId: FilePayload;
	bouwheerRatingInformation: string;
	eddHighRiskAndPepAnalysis: string;
	cpaPatDocumentId: FilePayload;
	cpaPatInformation: string;
};

export const submitNAPInternalCompliance = (
	body: NAPInternalCompliancePayload,
) => {
	return fetcher({ api: '/naps/frm/submit-internal-compliance', body });
};

export const verifyNAPInternalCompliance = (
	body: NAPInternalCompliancePayload,
) => {
	return fetcher({ api: '/naps/cca/verify-internal-compliance', body });
};

export type NAPSpecialConditionDetail = {
	id: number;
	napId: number;
	notes: string;
	verificationNotes: string;
	status: string;
	specialConditionSubmission: {
		id: number;
		proposal: string;
		finalApprover: number[];
		subject: MasterData;
		provision: string;
		specialCondition: string;
		specialConditionStatus: MasterData;
		evidenceFile: FileData;
	}[];
	finalApproverOptions: MasterData[];
	specialConditionSubjectOptions: MasterData[];
	specialConditionStatusOptions: MasterData[];
};

export const getNAPSpecialConditionDetail = ({
	napId,
	prospectId,
}: {
	napId: number;
	prospectId: string;
}) => {
	return fetcher<NAPSpecialConditionDetail>({
		api: '/naps/get-special-condition-submission-detail',
		body: {
			napId,
			prospectId,
		},
	});
};

export type NAPSpecialConditionPayload = {
	id?: number;
	napId: number;
	notes: string;
	specialConditionSubmissions: {
		id: number;
		subjectId: number;
		provision: string;
		proposal: string;
		specialCondition: string;
		specialConditionStatusId: number;
		finalApprover: number[];
		evidenceFile: FilePayload;
	}[];
};

export const submitNAPSpecialCondition = (body: NAPSpecialConditionPayload) => {
	return fetcher<string>({
		api: '/naps/frm/submit-special-condition-submission',
		body,
	});
};

export const verifyNAPSpecialCondition = (body: NAPSpecialConditionPayload) => {
	return fetcher<string>({
		api: '/naps/cca/verify-special-condition-submission',
		body,
	});
};

export type NAPRiskMitigationDetail = {
	id: number;
	prospectId: string;
	napId: number;
	status: string;
	action: string;
	verificationNotes: string;
	riskAndMitigations: { id: number; risk: string; mitigation: string }[];
};

export const getNAPRiskMitigationDetail = (prospectId: string) => {
	return fetcher<NAPRiskMitigationDetail>({
		api: '/naps/get-risk-mitigation-detail',
		body: { prospectId },
	});
};

export type NAPRecommendationDetail = {
	id: number;
	napId: number;
	status: string;
	action: string;
	verificationNotes: string;
	bppLimit: number;
	plafonPeriod: string;
	financingLimitType: MasterData;
	financingLimitTypeOptions: MasterData[];
	financingTypes: MasterData[];
	financingTypeOptions: MasterData[];
	financingObjects: MasterData[];
	financingObjectOptions: MasterData[];
	downPayment: string;
	effectiveInterestRate: string;
	tenor: number;
	administrationFee: string;
	provisionFee: string;
	fiduciaryFee: string;
	insurance: MasterData;
	insuranceOptions: MasterData[];
	insurancePayment: string; // not found in response
	servicingFee: string;
	paymentMethod: MasterData;
	paymentMethodOptions: MasterData[];
	categoryOptions: MasterData[];
	priorityOptions: MasterData[];
	evidenceFile: FileData;
	creditTermsAndConditions: {
		id: number;
		termAndCondition: string;
		category: MasterData;
		priority: MasterData;
	}[];
	specialCondition: string;
	creditCovenants: {
		// not found in response
		id: number;
		fileName: string;
		description: string;
	}[];
};

export const getNAPRecommendationDetail = (prospectId: string) => {
	return fetcher<NAPRecommendationDetail>({
		api: '/naps/get-recommendation-detail',
		body: { prospectId },
	});
};

export type NAPRecommendationPayload = {
	napId: number;
	verificationNotes: string;
	bppLimit: number;
	plafonPeriod: string;
	financingObjectIds: number[];
	financingLimitTypeId: number;
	downPayment: string;
	effectiveInterestRate: string;
	tenor: number;
	administrationFee: string;
	provisionFee: string;
	fiduciaryFee: string;
	insuranceId: number;
	servicingFee: string;
	insurancePayment: string;
	paymentMethodId: string;
	evidenceFile: FilePayload;
	specialCondition: string;
	creditTermsAndConditions: {
		id?: number;
		termAndCondition: string;
		categoryId: number;
		priorityId: number;
	}[];
};

export const submitNAPRecommendation = (body: NAPRecommendationPayload) => {
	return fetcher<{ napNo: string }>({
		api: '/naps/cca/submit-recommendation',
		body,
	});
};

export type NAPRiskMitigationPayload = {
	napId: number;
	verificationNotes: string;
	riskAndMitigations: { risk: string; mitigation: string }[];
};

export const submitNAPRiskMitigation = (body: NAPRiskMitigationPayload) => {
	return fetcher<{ napNo: string }>({
		api: '/naps/cca/submit-risk-mitigation',
		body,
	});
};

export type NAPApprovalPayload = {
	napId: number;
	action: string;
	returnTos: string[];
	notes: string;
};

export const submitNAPApproval = (body: NAPApprovalPayload) => {
	return fetcher({ api: '/naps/submit-approval', body });
};

export type NAPUser = {
	id: number;
	name: string;
	role: string;
	isAdditional: boolean;
};

export const getNAPUser = (body: FleetGetListParams) => {
	return fetcher<FleetPaginated<NAPUser>>({ api: '/naps/get-users', body });
};

export type NAPAuthorityMemberPayload = {
	prospectId: string;
	authorityLevelId: string;
};

export const getNAPAuthorityMember = (body: NAPAuthorityMemberPayload) => {
	return fetcher<{ proposers: NAPUser[]; committees: NAPUser[] }>({
		api: '/naps/get-authority-member',
		body,
	});
};

// NAP Analysis
export const getNAPAnalysis = (prospectId: string) => {
	return fetcher<NAPAnalysis>({
		api: '/naps/get-financial-analysis',
		body: { prospectId },
	});
};

export type NAPAnalysisAction = 'View' | 'Add' | 'Update' | 'Disabled';

export type NAPAnalysis = {
	financialAnalysisId?: number;
	napId: number;
	businessEntity: MasterData;
	legalAndManagementCategory: NAPAnalysisLegalAndManagementCategory;
	technicalCategory: NAPAnalysisTechnicalCategory;
	marketingCategory: NAPAnalysisMarketingCategory;
	financialCategory: NAPAnalysisFinancialCategory;
};

export type NAPAnalysisLegalAndManagementCategory = {
	action: NAPAnalysisAction;
	customerBusinessDocuments: CustomerBusinessDocument[];
	customerLegalDocuments: CustomerLegalDocument[];
	customerManagements: CustomerManagement[];
	guarantorCompanyLegalityDocuments: Array<
		CustomerLegalDocument & { napGuarantorCompanyLegalityDocumentId: number }
	>;
	legalCaseInformation: string;
	organizationAndWorkforceDocument: FileData;
	organizationAndWorkforceAdditionalInfo: string;
	debiturOtherInformation: string;
	notes: string;
	verificationNotes: string;
};

export type NAPSupplierTradeVerification = {
	id?: number;
	supplierName: string;
	verificationDate: string;
	contactPerson: string;
	verificationResult: string;
};

export type NAPAnalysisTechnicalCategory = {
	action: NAPAnalysisAction;
	businessPatternDocument: FileData;
	businessPatternAnalysisResult: string;
	productionProcess: string;
	officeLocation: string;
	siteLocation: string;
	productionRealizationAndProjectionAdditionalInfo: string;
	productionRealizationAndProjectionDocument: FileData;
	supplierTradeVerifications: NAPSupplierTradeVerification[];
	supplierTradeVerificationAdditionalInfo: string;
	needsAndAnalysisProductionUtilizationDocument: FileData;
	needsAndAnalysisProductionUtilizationAdditionalInfo: string;
	notes: string;
	verificationNotes: string;
};

export type NAPAnalysisMarketingCategory = {
	action: NAPAnalysisAction;
	employmentContracts: ProspectEmploymentContract[];
	employmentContractAdditionalInfo: string;
	salesAndWorkRealizationProjectionDocument: FileData;
	salesAndWorkRealizationProjectionAdditionalInfo: string;
	bouwheerTradeVerifications: NAPSupplierTradeVerification[];
	bouwheerTradeVerificationAdditionalInfo: string;
	notes: string;
	verificationNotes: string;
};

export type NAPKAPInformation = {
	id?: number;
	kap: string;
	year: string;
	opinion: string;
	description: string;
};

export type NAPAnalysisFinancialCategory = {
	action: NAPAnalysisAction;
	kapInformations: NAPKAPInformation[];
	balanceSheetDocument: FileData;
	balanceSheetAnalysisResult: string;
	profitAndLossDocument: FileData;
	profitAndLossAnalysisResult: string;
	financialRatioDocument: FileData;
	financialRatiosAnalysisResult: string;
	bankStatementAnalysisDocument: FileData;
	bankStatementAnalysisAdditionalInfo: string;
	repaymentCapacityDocument: FileData;
	repaymentCapacityAdditionalInfo: string;
	guarantorLetterDocument: FileData;
	guarantorEvaluationAdditionalInfo: string;
	cashFlowProjectionDocument: FileData;
	cashFlowProjectionAdditionalInfo: string;
	notes: string;
	verificationNotes: string;
};

export const submitNAPAnalysis = (body: NAPAnalysisPayload) => {
	return fetcher({ api: '/naps/frm/submit-financial-analysis', body });
};

export type NAPAnalysisPayload = {
	id?: number;
	napId: number;
	legalAndManagementCategory: NAPAnalysisLegalAndManagementCategoryPayload;
	technicalCategory: NAPAnalysisTechnicalCategoryPayload;
	marketingCategory: NAPAnalysisMarketingCategoryPayload;
	financialCategory: NAPAnalysisFinancialCategoryPayload;
};

export type NAPAnalysisLegalAndManagementCategoryPayload = {
	customerLegalDocuments: CustomerLegalDocument<'payload'>[];
	customerBusinessDocuments: CustomerBusinessDocument<'payload'>[];
	customerManagements: CustomerManagement<'payload'>[];
	guarantorCompanyLegalityDocuments: Array<
		CustomerLegalDocument<'payload'> & { documentType: string }
	>;
	legalCaseInformation: string;
	organizationAndWorkforceDocument: FilePayload;
	organizationAndWorkforceAdditionalInfo: string;
	debiturOtherInformation: string;
	notes: string;
	verificationNotes: string;
};

export type NAPAnalysisTechnicalCategoryPayload = {
	businessPatternDocument: FilePayload;
	businessPatternAnalysisResult: string;
	productionProcess: string;
	productionRealizationAndProjectionDocument: FilePayload;
	productionRealizationAndProjectionAdditionalInfo: string;
	needsAndAnalysisProductionUtilizationDocument: FilePayload;
	needsAndAnalysisProductionUtilizationAdditionalInfo: string;
	supplierTradeVerifications: NAPSupplierTradeVerification[];
	supplierTradeVerificationAdditionalInfo: string;
	notes: string;
	verificationNotes: string;
};

export type NAPAnalysisMarketingCategoryPayload = {
	employmentContracts: ProspectEmploymentContract<'payload'>[];
	employmentContractAdditionalInfo: string;
	salesAndWorkRealizationProjectionDocument: FilePayload;
	salesAndWorkRealizationProjectionAdditionalInfo: string;
	bouwheerTradeVerifications: NAPSupplierTradeVerification[];
	bouwheerTradeVerificationAdditionalInfo: string;
	notes: string;
	verificationNotes: string;
};

export type NAPAnalysisFinancialCategoryPayload = {
	kapInformations: NAPKAPInformation[];
	balanceSheetDocument: FilePayload;
	balanceSheetAnalysisResult: string;
	profitAndLossDocument: FilePayload;
	profitAndLossAnalysisResult: string;
	financialRatioDocument: FilePayload;
	financialRatiosAnalysisResult: string;
	bankStatementAnalysisDocument: FilePayload;
	bankStatementAnalysisAdditionalInfo: string;
	repaymentCapacityDocument: FilePayload;
	repaymentCapacityAdditionalInfo: string;
	guarantorLetterDocument: FilePayload;
	guarantorEvaluationAdditionalInfo: string;
	cashFlowProjectionDocument: FilePayload;
	cashFlowProjectionAdditionalInfo: string;
	notes: string;
	verificationNotes: string;
};

export const verifyNAPAnalysis = (body: NAPAnalysisPayload) => {
	return fetcher({ api: '/naps/cca/verify-financial-analysis', body });
};

export type SCRisalah = {
	napResolutionId: number;
	no: number;
	napId: number;
	napNo: string;
	spfNo: string | null;
	customer: MasterData;
	customerGroup: MasterData;
	rkpEstimatedDate: string;
	rkpDate: string;
	bodResolutionDate: string;
	bodResolutionFullApprovalDate: string;
	viewResolutionDocument: boolean;
	resolutionDocument: FleetFile;
	inputResolution: boolean;
	npwpNo: string;
	prospectId: string;
	frm: MasterData;
	fbdh: MasterData;
	division: MasterData;
	plafonType: MasterData;
	financingLimitType: MasterData;
	lastModifiedAt: string | null;
	lastModifiedBy: string | null;
};

export type GetSCRisalahParam = FleetQueryParams & {
	frmId?: number;
	fbdhId?: number;
	divisionId?: number;
	plafonTypeId?: number;
	financingLimitTypeId?: number;
};

export const getSCRisalah = (params?: GetSCRisalahParam) => {
	return fetcher<FleetPaginated<SCRisalah>>({
		api: '/naps/resolutions/get-list',
		body: params,
	});
};

export const updateRkpEstimatedDate = (body: {
	rkpEstimatedDate: string;
	napNo: string;
}) => {
	return fetcher({ api: '/naps/resolutions/submit-rkp-estimated-date', body });
};

export type SCRisalahDetail = {
	napId: number;
	napNo: string;
	customerId: string;
	customerName: string;
	customerGroupId: string;
	customerGroupName: string;
	rkpDate: string;
	bodResolutionDate: string;
	bodResolutionFullApprovalDate: string;
	resolutionFile: FileData;
	creditTermsAndConditions: Array<{
		id: number;
		termAndCondition: string;
		category: MasterData;
		priority: MasterData;
	}>;
	categoryOptions: MasterData[];
	priorityOptions: MasterData[];
	napApprovalHistory: Array<{
		id: number;
		name: string;
		date: string;
		process: string;
		status: string;
		notes: string;
	}>;
	notes: string;
	requestStatus: MasterData;
	ultimateOwnerName: string;
	facilityValidityPeriod: string;
	plafons: {
		id: number;
		isDefault: true;
		plafonType: MasterData;
		financingTypesId: number[];
		facilityTypesId: number[];
		requestedLimit: number;
		approvedLimit: number;
		financingLimitType: MasterData;
		financingObjectsId: number[];
	}[];
	covenants: {
		id: number;
		covenant: string;
		category: MasterData;
		priority: MasterData;
		repeatFrequency: MasterData;
		repeatFrequencyCustom: {
			id: number;
			repeatValue: number;
			repeatFrequency: MasterData;
			onPattern: {
				dayOfMonth: number;
				month: MasterData;
			};
			onThePattern: {
				position: MasterData;
				dayOfWeek: MasterData;
				month: MasterData;
			};
		};
		monitoringType: MasterData;
		furtherAnalysis: MasterData;
		dueDate: string | null;
		covenantDocument: FleetFile;
	}[];
	covenantCategoryOptions: MasterData[];
	financingTypeOptions: MasterData[];
	facilityTypeOptions: MasterData[];
	financingLimitTypeOptions: MasterData[];
	financingObjectOptions: MasterData[];
	furtherAnalysisOptions: MasterData[];
	monitoringType: MasterData[];
	repeatFrequencyOptions: MasterData[];
	customRepeatFrequencyOptions: MasterData[];
	monthOptions: MasterData[];
	positionOptions: MasterData[];
	dayOfWeekOptions: MasterData[];
};

export const getSCRisalahDetail = (prospectId: string) => {
	return fetcher<SCRisalahDetail>({
		api: '/naps/resolutions/get-detail',
		body: { prospectId },
	});
};

export type SubmitRisalahPayload = {
	napNo: string;
	rkpDate: string;
	bodResolutionDate: string;
	bodResolutionFullApprovalDate: string;
	facilityValidityPeriod: string;
	resolutionFile: FilePayload;
	plafons: {
		id: number;
		isDefault: boolean;
		plafonTypeId: number;
		financingTypesId: number[];
		facilityTypesId: number[];
		approvedLimit: number;
		financingLimitTypeId: number;
		financingObjectsId: number[];
		requestedLimit: number;
	}[];
	covenants: {
		id: number;
		covenant: string;
		categoryId: number;
		priorityId: number;
		repeatFrequencyId: number;
		monitoringTypeId: number;
		furtherAnalysisId: number;
		repeatFrequencyCustom: {
			id: number;
			repeatValue: number;
			repeatFrequencyId: number;
			onPattern: {
				dayOfMonth: number;
				monthId: number;
			};
			onThePattern: {
				positionId: number;
				dayOfWeekId: number;
				monthId: number;
			};
		};
		dueDate: string | null;
		covenantDocument: FilePayload;
	}[];
	creditTermsAndConditions: Array<{
		id: number;
		termAndCondition: string;
		categoryId: number;
		priorityId: number;
	}>;
	notes: string;
	requestStatus: number;
};

export const submitSCRisalah = (body: SubmitRisalahPayload) => {
	return fetcher({ api: '/naps/resolutions/submit', body });
};

// NAP Summary Consent
export const getNAPSummaryConsent = (prospectId: string) => {
	return fetcher<NAPSummaryConsent>({
		api: '/naps/get-summary-consent-detail',
		body: { prospectId },
	});
};

export type SummaryConsentDoc = {
	docId: number;
	docName: string;
	docSlug: string;
	document: FileData;
};

export type NAPSummaryConsent = {
	id?: number;
	napId?: number;
	companyProfileSummary: string;
	companyProfileSummaryFile: FileData;
	bmriHistory: string;
	bmriHistoryFile: FileData;
	financingRequirementSummary: string;
	financingRequirementSummaryFile: FileData;
	balanceSheetAnalysis: string;
	balanceSheetAnalysisFile: FileData;
	profitLossAnalysis: string;
	profitLossAnalysisFile: FileData;
	cashFlowAnalysis: string;
	cashFlowAnalysisFile: FileData;
	financialRatioAnalysis: string;
	financialRatioAnalysisFile: FileData;
	slikAnalysis: string;
	slikAnalysisFile: FileData;
	notes: string;
	verificationNotes: string;
	summaryConsentDocs: SummaryConsentDoc[];
};

export const submitNAPSummaryConsent = (body: NAPSummaryConsentPayload) => {
	return fetcher({ api: '/naps/frm/submit-summary-consent', body });
};

export type NAPSummaryConsentPayload = {
	id?: number;
	napId?: number;
	companyProfileSummary: string;
	companyProfileSummaryFile: FilePayload;
	bmriHistory: string;
	bmriHistoryFile: FilePayload;
	financingRequirementSummary: string;
	financingRequirementSummaryFile: FilePayload;
	balanceSheetAnalysis: string;
	balanceSheetAnalysisFile: FilePayload;
	profitLossAnalysis: string;
	profitLossAnalysisFile: FilePayload;
	cashFlowAnalysis: string;
	cashFlowAnalysisFile: FilePayload;
	financialRatioAnalysis: string;
	financialRatioAnalysisFile: FilePayload;
	slikAnalysis: string;
	slikAnalysisFile: FilePayload;
	notes: string;
	verificationNotes: string;
};

export const verifyNAPSummaryConsent = (body: NAPSummaryConsentPayload) => {
	return fetcher({ api: '/naps/cca/verify-summary-consent', body });
};

// NAP EDD PEP Analysis
export const getNAPEDDPEPAnalysis = (prospectId: string) => {
	return fetcher<NAPEDDPEPAnalysis>({
		api: '/naps/get-edd-pep-anlysis-detail',
		body: { prospectId },
	});
};

export const verifyNAPEDDPEPAnalysis = (body: NAPEDDPEPAnalysis) => {
	return fetcher({ api: '/naps/cca/verify-edd-pep-analysis', body });
};

export type NAPEDDPEPAnalysis = {
	id: number;
	napId: number;
	analysisResult: string;
};
